import React, { useState, FormEvent, ChangeEvent, useEffect } from "react";
import { applySignupCode, signin, signup, verifyOtp } from "../services/apis";
import { Eye, EyeOff } from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import Cookies from "js-cookie";
import { AREA_CODES } from "../assests/Areacodes";
import { PasswordResetFlow } from "./PasswordResetFlow";
import { getPendingSignupCode } from "../utils/signupLink";
const DEFAULT_COUNTRY_CODE = "+1";

const formatPhone = (raw: string, code: string): string => {
  const cleaned = raw.replace(/\D/g, "");

  if (code === "+1") {
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6)
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  }

  return cleaned;
};

const validatePhone = (code: string, number: string): string => {
  const cleaned = number.replace(/\D/g, "");

  if (code === "+1") {
    if (cleaned.length < 10) return "Phone number must be 10 digits";
    if (cleaned.length > 10) return "Phone number must be exactly 10 digits";
    const area = parseInt(cleaned.slice(0, 3));
    console.log("Validating area code:", area);
    if (!AREA_CODES.US.includes(area) && !AREA_CODES.CA.includes(area)) {
      return "Invalid phone number. Please enter a valid number.";
    }
    return "";
  }

  return "";
};

// Type definitions
interface FormData {
  name: string;
  email: string;
  phonenumber: string;
  password: string;
  countryCode: string;
  confirmPassword: string;
  promotionalEmails: boolean;
  promotionalTexts: boolean;
}

interface FormErrors {
  name?: string;
  email?: string;
  phonenumber?: string;
  password?: string;
  otp?: string;
  confirmPassword?: string;
}
interface FormTouched {
  name?: boolean;
  email?: boolean;
  phonenumber?: boolean;
  password?: boolean;
  otp?: boolean;
  confirmPassword?: boolean;
}

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  signupDailog?: boolean;
  activeTab?: "login" | "signup";
  lockToSignup?: boolean;
  onSignupSuccess?: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  signupDailog = true,
  activeTab,
  lockToSignup = false,
  onSignupSuccess,
}) => {
  const [isSignup, setIsSignup] = useState<boolean>(false);
  const [isOtpStep, setIsOtpStep] = useState<boolean>(false);
  const [isResetFlowOpen, setIsResetFlowOpen] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const { login } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phonenumber: "",
    password: "",
    countryCode: DEFAULT_COUNTRY_CODE,
    confirmPassword: "",
    promotionalEmails: true,
    promotionalTexts: true,
  });
  const [otp, setOtp] = useState<string>("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<FormTouched>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string>("");
  const [redeemSuccessPopup, setRedeemSuccessPopup] = useState<{
    amountRedeemed: number;
    newBalance: number;
  } | null>(null);
  const [pendingVerifiedUser, setPendingVerifiedUser] = useState<any>(null);

  // Reset modal
  const resetModal = () => {
    setFormData({
      name: "",
      email: "",
      phonenumber: "",
      password: "",
      countryCode: DEFAULT_COUNTRY_CODE,
      confirmPassword: "",
      promotionalEmails: true,
      promotionalTexts: true,
    });
    setOtp("");
    setErrors({});
    setTouched({});
    setIsOtpStep(false);
    setIsResetFlowOpen(false);
    setAuthError("");
    setRedeemSuccessPopup(null);
    setPendingVerifiedUser(null);
  };
  useEffect(() => {
    if (activeTab) {
      setIsSignup(activeTab === "signup");
    }
  }, [activeTab]);

  useEffect(() => {
    if (!lockToSignup) return;
    setIsSignup(true);
    setIsResetFlowOpen(false);
  }, [lockToSignup]);

  const switchMode = (): void => {
    if (lockToSignup) return;
    setIsSignup(!isSignup);
    resetModal();
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ): void => {
    const { name, value } = e.target;

    let updatedFormData = { ...formData };

    if (name === "phonenumber") {
      const formatted = formatPhone(value, formData.countryCode);
      updatedFormData.phonenumber = formatted;
    } else {
      updatedFormData = { ...updatedFormData, [name]: value };
    }
    setFormData(updatedFormData);
    setTouched((prev) => ({ ...prev, [name]: true }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    if (authError) {
      setAuthError("");
    }
  };
  const getFieldTone = (value: string, isTouched?: boolean): string => {
    if (!value) return "dm-auth-empty";
    return isTouched ? "dm-auth-filled" : "dm-auth-prefilled";
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>,
  ): void => {
    const { name, value } = e.target;
    const nextErrors: FormErrors = {};

    if (name === "name") {
      if (!value.trim()) nextErrors.name = "Name is required";
    }

    if (name === "phonenumber") {
      if (!formData.phonenumber) nextErrors.phonenumber = "Phone is required";
      else {
        const phoneError = validatePhone(
          formData.countryCode,
          formData.phonenumber,
        );
        if (phoneError) nextErrors.phonenumber = phoneError;
      }
    }

    if (name === "email") {
      if (!value.trim()) nextErrors.email = "Email is required";
      else if (!/^\S+@\S+\.\S+$/.test(value)) {
        nextErrors.email = "Please enter a valid email";
      }
    }

    if (name === "password") {
      if (!value) nextErrors.password = "Password is required";
      else if (
        isSignup &&
        !/^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*]).{8,}$/.test(value)
      ) {
        nextErrors.password =
          "Password must be 8+ characters including uppercase, number, and special character";
      }

      if (isSignup && formData.confirmPassword) {
        if (value !== formData.confirmPassword) {
          nextErrors.confirmPassword = "Passwords do not match";
        }
      }
    }

    if (name === "confirmPassword") {
      if (isSignup && value && value !== formData.password) {
        nextErrors.confirmPassword = "Passwords do not match";
      }
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...nextErrors }));
    }
  };
  const validateForm = (): FormErrors => {
    const newErrors: FormErrors = {};

    if (isSignup) {
      if (!formData.name.trim()) newErrors.name = "Name is required";

      const phoneError = validatePhone(
        formData.countryCode,
        formData.phonenumber,
      );
      if (!formData.phonenumber) newErrors.phonenumber = "Phone is required";
      else if (phoneError) newErrors.phonenumber = phoneError;
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (
      isSignup &&
      !/^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*]).{8,}$/.test(formData.password)
    ) {
      newErrors.password =
        "Password must be 8+ characters including uppercase, number, and special character";
    }
    if (isSignup && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    return newErrors;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    setLoading(true);
    e.preventDefault();
    setAuthError("");
    const newErrors = validateForm();

    if (Object.keys(newErrors).length === 0) {
      try {
        if (isSignup) {
          const cleanedPhone = formData.phonenumber.replace(/\D/g, "");
          const data = {
            name: formData.name,
            email: formData.email.toLowerCase(),
            phonenumber: cleanedPhone,
            password: formData.password,
            countryCode: formData.countryCode,
            promotionalEmails: formData.promotionalEmails,
            promotionalTexts: formData.promotionalTexts,
          };
          const response = await signup(data);
          setLoading(false);

          toast.success(
            "OTP sent to your register E-mail! Please verify your OTP.",
          );
          setIsOtpStep(true);
        } else {
          const response = await signin({
            email: formData.email.toLowerCase(),
            password: formData.password,
          });
          setLoading(false);
          if (response.data.success) {
            toast.success("Login successful!");
            Cookies.set("Logintoken", response.data.token);
            onClose();
            resetModal();
            login(response.data.user);
          } else {
            setAuthError(response.data.message || "Login failed!");
          }
        }
      } catch (error: any) {
        setLoading(false);
        setAuthError(error.response?.data?.message || "Something went wrong!");
      }
    } else {
      setLoading(false);
      setErrors(newErrors);
    }
  };

  const completeVerifiedSignup = (verifiedUser: any) => {
    toast.success("OTP verified successfully!");
    onSignupSuccess?.();
    onClose();
    resetModal();
    login(verifiedUser);
  };

  // Handle OTP verification
  const handleVerifyOtp = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!otp.trim()) {
      setErrors({ otp: "OTP is required" });
      return;
    }
    setAuthError("");
    try {
      const cleanedPhone = formData.phonenumber.replace(/\D/g, "");
      const response = await verifyOtp({
        phonenumber: cleanedPhone,
        otp,
      });
      Cookies.set("Logintoken", response.data.token);

      const createdUserId = response?.data?.user?.id;
      const pendingSignupCode = getPendingSignupCode();

      if (createdUserId && pendingSignupCode) {
        try {
          const signupCodeResponse = await applySignupCode({
            userId: createdUserId,
            code: pendingSignupCode,
          });

          const redemption = signupCodeResponse?.data?.redemption;
          const amountRedeemed = Number(redemption?.amountRedeemed);
          const newBalance = Number(redemption?.newBalance);

          if (
            signupCodeResponse?.data?.success &&
            Number.isFinite(amountRedeemed) &&
            Number.isFinite(newBalance)
          ) {
            setPendingVerifiedUser(response.data.user);
            setRedeemSuccessPopup({
              amountRedeemed,
              newBalance,
            });
            return;
          }
        } catch (signupCodeError) {
          // Keep signup success flow intact even if code API fails.
        }
      }

      completeVerifiedSignup(response.data.user);
    } catch (error: any) {
      setAuthError(error.response?.data?.message || "Invalid OTP!");
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (redeemSuccessPopup) return;
    if (e.target === e.currentTarget) {
      onClose();
      resetModal();
    }
  };

  const handleRedeemPopupContinue = () => {
    if (!pendingVerifiedUser) return;
    const verifiedUser = pendingVerifiedUser;
    setRedeemSuccessPopup(null);
    setPendingVerifiedUser(null);
    completeVerifiedSignup(verifiedUser);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-[9999] p-4"
      onClick={handleBackdropClick}
    >
      {!redeemSuccessPopup && (
        <div className="dm-auth-modal bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto transition-all duration-300">
          {/* Header */}
          <div className="flex justify-between items-center px-6 py-3 border-b">
            <h2 className="text-2xl font-bold text-gray-800">
              {isResetFlowOpen
                ? "Reset Password"
                : isOtpStep
                  ? "Verify OTP"
                  : isSignup
                    ? "Sign Up"
                    : "Sign In"}
            </h2>
            <button
              onClick={() => {
                onClose();
                resetModal();
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close modal"
            >
              ✕
            </button>
          </div>

          <div className="p-6">
            {isResetFlowOpen ? (
              <PasswordResetFlow
                initialEmail={formData.email}
                onCancel={() => setIsResetFlowOpen(false)}
                onComplete={(email) => {
                  setFormData((prev) => ({ ...prev, email }));
                  setIsSignup(false);
                }}
              />
            ) : !isOtpStep ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                {authError && (
                  <p className="text-red-500 text-sm">{authError}</p>
                )}
                {isSignup && (
                  <>
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="John Doe"
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${
                          errors.name ? "border-red-500" : "border-gray-300"
                        } ${getFieldTone(formData.name, touched.name)}`}
                      />
                      {errors.name && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.name}
                        </p>
                      )}
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <div className="flex gap-2">
                        <div className="border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-700 flex items-center">
                          {DEFAULT_COUNTRY_CODE}
                        </div>
                        <input
                          type="tel"
                          name="phonenumber"
                          value={formData.phonenumber}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="123-456-7890"
                          className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none ${
                            errors.phonenumber
                              ? "border-red-500"
                              : "border-gray-300"
                          } ${getFieldTone(
                            formData.phonenumber,
                            touched.phonenumber,
                          )}`}
                        />
                      </div>
                      {errors.phonenumber && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.phonenumber}
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="john@example.com"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${
                      errors.email ? "border-red-500" : "border-gray-300"
                    } ${getFieldTone(formData.email, touched.email)}`}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                  )}
                </div>

                {/* Password */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="••••••••"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${
                      errors.password ? "border-red-500" : "border-gray-300"
                    } ${getFieldTone(formData.password, touched.password)}`}
                  />
                  <span
                    className="absolute right-3 top-9 cursor-pointer text-gray-500"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </span>

                  {errors.password && isSignup && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.password}
                    </p>
                  )}
                </div>
                {!isSignup && (
                  <div className="flex justify-end -mt-1">
                    <button
                      type="button"
                      onClick={() => setIsResetFlowOpen(true)}
                      className="text-xs font-medium text-gray-600 hover:text-black"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}
                {/* Confirm Password Field */}
                {isSignup && (
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm Password
                    </label>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="••••••••"
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${
                        errors.confirmPassword
                          ? "border-red-500"
                          : "border-gray-300"
                      } ${getFieldTone(
                        formData.confirmPassword,
                        touched.confirmPassword,
                      )}`}
                    />
                    <span
                      className="absolute right-3 top-9 cursor-pointer text-gray-500"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={20} />
                      ) : (
                        <Eye size={20} />
                      )}
                    </span>
                    {errors.confirmPassword && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.confirmPassword}
                      </p>
                    )}
                    <div className="flex flex-col mt-3 gap-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.promotionalEmails}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              promotionalEmails: e.target.checked,
                            }))
                          }
                          className="w-6 h-6 cursor-pointer dm-info-checkbox appearance-none border border-gray-400 rounded-md 
                                checked:bg-black checked:border-black
                                checked:after:content-['✓'] checked:after:text-white 
                                checked:after:flex checked:after:items-center 
                                checked:after:justify-center checked:after:text-sm
                                flex items-center justify-center"
                        />
                        <span className="text-sm dm-auth-consent">
                          Receive promotional emails
                        </span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.promotionalTexts}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              promotionalTexts: e.target.checked,
                            }))
                          }
                          className="w-6 h-6 cursor-pointer dm-info-checkbox appearance-none border border-gray-400 rounded-md 
                                checked:bg-black checked:border-black
                                checked:after:content-['✓'] checked:after:text-white 
                                checked:after:flex checked:after:items-center 
                                checked:after:justify-center checked:after:text-sm
                                flex items-center justify-center"
                        />
                        <span className="text-sm dm-auth-consent">
                          Receive promotional text messages
                        </span>
                      </label>
                    </div>
                  </div>
                )}
                {/* Submit */}
                <button
                  type="submit"
                  disabled={
                    loading ||
                    Object.keys(errors).some(
                      (key) => errors[key as keyof FormErrors],
                    )
                  }
                  className="w-full bg-black dm-white-btn text-white py-2 px-4 rounded-lg font-medium "
                >
                  {isSignup ? "Sign Up" : "Sign In"}
                </button>
              </form>
            ) : (
              // OTP Step
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                {authError && (
                  <p className="text-red-500 text-sm">{authError}</p>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Enter OTP
                  </label>
                  <input
                    type="text"
                    name="otp"
                    value={otp}
                    onChange={(e) => {
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                      setTouched((prev) => ({ ...prev, otp: true }));
                      if (errors.otp) {
                        setErrors((prev) => ({ ...prev, otp: undefined }));
                      }
                      if (authError) {
                        setAuthError("");
                      }
                    }}
                    placeholder="6-digit OTP"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${
                      errors.otp ? "border-red-500" : "border-gray-300"
                    } ${getFieldTone(otp, touched.otp)}`}
                  />
                  {errors.otp && (
                    <p className="text-red-500 text-xs mt-1">{errors.otp}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full bg-black text-white dm-white-btn py-2 px-4 rounded-lg font-medium "
                >
                  Verify OTP
                </button>

                <button
                  type="button"
                  onClick={() => setIsOtpStep(false)}
                  className="w-full border border-gray-300 text-black dm-white-btn py-2 px-4 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Back to Signup
                </button>
              </form>
            )}

            {/* Switch */}
            {!isOtpStep && !isResetFlowOpen && !lockToSignup && (
              <>
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-white px-4 text-gray-500">
                      {isSignup
                        ? "Already have an account?"
                        : "Don't have an account?"}
                    </span>
                  </div>
                </div>

                <button
                  onClick={switchMode}
                  className="dm-white-btn w-full border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 font-medium"
                >
                  {isSignup ? "Sign In" : "Create Account"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
      {redeemSuccessPopup && (
        <div className="fixed inset-0 z-[10000] bg-black/25 backdrop-blur-[1px] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl p-7 text-center">
            <h3 className="text-3xl font-extrabold text-green-600 mb-3">
              Hurray!
            </h3>
            <p className="text-gray-900 text-lg font-semibold mb-2">
              You unlocked {redeemSuccessPopup.amountRedeemed.toFixed(2)} points
            </p>
            <p className="text-gray-700 mb-6">
              You can see it in your wallet.
              {redeemSuccessPopup.newBalance.toFixed(2)}
            </p>
            <button
              type="button"
              onClick={handleRedeemPopupContinue}
              className="w-full bg-black toggle-selected-text py-3 rounded-xl font-semibold hover:bg-gray-800 transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export { AuthModal };
